import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MessageSquare, Send, Trash2, AlertOctagon } from "lucide-react";

export default function Comments({ bugId }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get(`/bugs/${bugId}/comments`);
      setItems(data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bugId]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const t = body.trim();
    if (t.length < 3) { setErr("Mínimo 3 caracteres. Explícate mejor, joven."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/bugs/${bugId}/comments`, { body: t });
      setItems((s) => [...s, data]);
      setBody("");
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    try {
      await api.delete(`/comments/${id}`);
      setItems((s) => s.filter((c) => c.id !== id));
    } catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div className="mt-8" data-testid="comments-section">
      <div className="flex items-center gap-2 text-red-500 text-xs uppercase tracking-[0.3em] mb-3">
        <MessageSquare size={14} />
        <span>comentarios ({items.length})</span>
      </div>

      <div className="term-panel p-4 space-y-3 mb-4">
        {loading && <div data-testid="comments-loading" className="text-red-500 text-xs blink-cursor">Cargando</div>}
        {!loading && items.length === 0 && (
          <div data-testid="comments-empty" className="text-neutral-500 text-xs uppercase tracking-[0.2em]">
            // sin comentarios aún. sé el primero en aclarar el reporte.
          </div>
        )}
        {Array.isArray(items) && items.map((c) => (
          <div key={c.id} data-testid={`comment-${c.id}`} className="border border-red-500/20 p-3">
            <div className="flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.2em] text-red-500/80 mb-1">
              <span className="text-neutral-200">{c.author_name}</span>
              <span className="text-neutral-500">·</span>
              <span className="text-neutral-500">{new Date(c.created_at).toLocaleString()}</span>
              {(user?.id === c.author_id || user?.role === "admin") && (
                <button
                  data-testid={`comment-delete-${c.id}`}
                  onClick={() => del(c.id)}
                  className="ml-auto text-neutral-500 hover:text-red-500 transition-colors"
                  aria-label="Borrar comentario"
                >
                  <Trash2 size={12} />
                </button>
              )}
            </div>
            <div className="text-neutral-100 whitespace-pre-wrap text-sm">{c.body}</div>
          </div>
        ))}
      </div>

      <form onSubmit={submit} data-testid="comment-form" className="term-panel p-4">
        <label className="term-label">Añadir comentario</label>
        <textarea
          data-testid="comment-input"
          className="term-input"
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Escribe tu comentario para aclarar el bug..."
        />
        {err && (
          <div data-testid="comment-error" className="flex items-start gap-1.5 text-red-500 text-xs uppercase tracking-widest mt-2 shake">
            <AlertOctagon size={12} className="mt-0.5" />
            <span className="glitch">{err}</span>
          </div>
        )}
        <div className="flex items-center gap-3 mt-3">
          <button
            data-testid="comment-submit-btn"
            type="submit"
            disabled={busy || body.trim().length < 3}
            className="term-btn"
          >
            <Send size={12} /> {busy ? "> enviando" : "> Publicar"}
          </button>
          <span className="text-[0.65rem] uppercase tracking-[0.2em] text-neutral-500">
            {body.length}/2000
          </span>
        </div>
      </form>
    </div>
  );
}
